import defaults from "@/lib/ai/server/provider-defaults.json";

export const AI_CUSTOM_PROVIDERS = [
	"openrouter",
	"openai",
	"openai-compatible",
	"anthropic",
	"google",
] as const;
export const DEFAULT_SERVER_PROVIDER = "server-default";
const AI_PROVIDER_VALUES = [
	DEFAULT_SERVER_PROVIDER,
	...AI_CUSTOM_PROVIDERS,
] as const;

export type AiProvider = (typeof AI_PROVIDER_VALUES)[number];

export interface AiClientSettings {
	provider: AiProvider;
	apiKey: string;
	model: string;
	baseURL: string;
}

export interface AiCustomConnection {
	id: string;
	label: string;
	provider: Exclude<AiProvider, "server-default">;
	apiKey: string;
	baseURL: string;
	models: string[];
	lastValidatedAt: number;
}

export interface AiEntryContext {
	source:
		| "desktop-float"
		| "desktop-nav"
		| "mobile-nav"
		| "mobile-header-menu"
		| "direct";
	pathname: string;
	label: string;
	timestamp: number;
}

export const AI_SETTINGS_STORAGE_KEY = "odin.ai.settings";
export const AI_CUSTOM_CONNECTIONS_STORAGE_KEY = "odin.ai.custom-connections";
export const AI_ENTRY_CONTEXT_STORAGE_KEY = "odin.ai.entry-context";
export const AI_ENTRY_CONTEXT_MAX_AGE_MS = 1000 * 60 * 60 * 6;
export const DEFAULT_SERVER_MODEL = defaults.defaultModel;
export const PLATFORM_DEFAULT_MODEL_LABEL_ZH = "默认模型";
export const PLATFORM_DEFAULT_MODEL_LABEL_EN = "Default model";
export const DEFAULT_OPENROUTER_MODEL = defaults.defaultModel;
export const DEFAULT_OPENAI_MODEL = "gpt-5.4-mini";
export const DEFAULT_OPENAI_COMPATIBLE_MODEL = "deepseek-v4-flash";
export const DEFAULT_ANTHROPIC_MODEL = "claude-sonnet-4-6";
export const DEFAULT_GOOGLE_MODEL = "gemini-3-flash-preview";

export interface AiProviderOption {
	value: AiProvider;
	label: string;
	description: string;
	defaultBaseURL: string;
}

export interface AiProviderGuide {
	title: string;
	notes: string[];
	examples: string[];
}

export interface AiModelOption {
	value: string;
	label: string;
}

export const aiProviderOptions: AiProviderOption[] = [
	{
		value: DEFAULT_SERVER_PROVIDER,
		label: "Platform Default",
		description: "Use the platform-managed OpenRouter smart route",
		defaultBaseURL: "",
	},
	{
		value: "openrouter",
		label: "OpenRouter",
		description: "Use your own OpenRouter key. Defaults to DeepSeek V4 Flash.",
		defaultBaseURL: "https://openrouter.ai/api/v1",
	},
	{
		value: "openai",
		label: "OpenAI",
		description: "Use your own OpenAI key and endpoint.",
		defaultBaseURL: "https://api.openai.com/v1",
	},
	{
		value: "openai-compatible",
		label: "OpenAI Compatible",
		description:
			"DeepSeek V4 preset. Change Base URL and model together for Qwen, SiliconFlow, Together, Groq, Ollama, or a self-hosted gateway.",
		defaultBaseURL: "https://api.deepseek.com",
	},
	{
		value: "anthropic",
		label: "Anthropic",
		description: "Use your own Anthropic key and endpoint.",
		defaultBaseURL: "https://api.anthropic.com/v1",
	},
	{
		value: "google",
		label: "Google",
		description: "Use your own Google Generative AI key and endpoint.",
		defaultBaseURL: "https://generativelanguage.googleapis.com/v1beta",
	},
];

export const aiModelOptionsByProvider: Record<AiProvider, AiModelOption[]> = {
	"server-default": [
		{
			value: DEFAULT_SERVER_MODEL,
			label: "DeepSeek V4 Flash Smart Route",
		},
	],
	openrouter: [
		{
			value: DEFAULT_OPENROUTER_MODEL,
			label: "DeepSeek V4 Flash",
		},
		{
			value: "deepseek/deepseek-v4-flash:free",
			label: "DeepSeek V4 Flash Free",
		},
		{
			value: "openrouter/free",
			label: "OpenRouter Free Router",
		},
	],
	openai: [
		{
			value: DEFAULT_OPENAI_MODEL,
			label: "GPT-5.4 Mini",
		},
		{
			value: "gpt-5.5",
			label: "GPT-5.5",
		},
		{
			value: "gpt-5.4",
			label: "GPT-5.4",
		},
		{
			value: "gpt-5.4-nano",
			label: "GPT-5.4 Nano",
		},
	],
	"openai-compatible": [
		{
			value: DEFAULT_OPENAI_COMPATIBLE_MODEL,
			label: "DeepSeek V4 Flash",
		},
		{
			value: "deepseek-v4-pro",
			label: "DeepSeek V4 Pro",
		},
		{
			value: "qwen3.6-plus",
			label: "Qwen3.6 Plus",
		},
		{
			value: "qwen3.6-flash",
			label: "Qwen3.6 Flash",
		},
		{
			value: "qwen3.6-max-preview",
			label: "Qwen3.6 Max Preview",
		},
	],
	anthropic: [
		{
			value: DEFAULT_ANTHROPIC_MODEL,
			label: "Claude Sonnet 4.6",
		},
		{
			value: "claude-opus-4-7",
			label: "Claude Opus 4.7",
		},
		{
			value: "claude-haiku-4-5",
			label: "Claude Haiku 4.5",
		},
	],
	google: [
		{
			value: DEFAULT_GOOGLE_MODEL,
			label: "Gemini 3 Flash Preview",
		},
		{
			value: "gemini-3-pro-preview",
			label: "Gemini 3 Pro Preview",
		},
		{
			value: "gemini-2.5-flash",
			label: "Gemini 2.5 Flash Stable",
		},
		{
			value: "gemini-2.5-flash-lite",
			label: "Gemini 2.5 Flash-Lite Stable",
		},
	],
};

export function getDefaultModelForProvider(provider: AiProvider) {
	return aiModelOptionsByProvider[provider][0]?.value ?? "";
}

export function getEffectiveModelForProvider(
	provider: AiProvider,
	model?: string | null,
) {
	if (provider === DEFAULT_SERVER_PROVIDER) {
		return getDefaultModelForProvider(provider);
	}

	return model?.trim() || getDefaultModelForProvider(provider);
}

export function getDefaultBaseURLForProvider(provider: AiProvider) {
	return (
		aiProviderOptions.find((option) => option.value === provider)
			?.defaultBaseURL ?? ""
	);
}

export function getAiModelOptions(provider: AiProvider) {
	return aiModelOptionsByProvider[provider] ?? [];
}

export function isKnownAiModelOption(
	provider: AiProvider,
	model?: string | null,
) {
	const effectiveModel = model?.trim();
	if (!effectiveModel) {
		return false;
	}

	return getAiModelOptions(provider).some(
		(item) => item.value === effectiveModel,
	);
}

export function getProviderLabel(provider: AiProvider) {
	return (
		aiProviderOptions.find((option) => option.value === provider)?.label ??
		"Server Default"
	);
}

export function getModelLabel(provider: AiProvider, model: string) {
	const effectiveModel = getEffectiveModelForProvider(provider, model);
	const option = getAiModelOptions(provider).find(
		(item) => item.value === effectiveModel,
	);
	if (option) return option.label;
	if (!effectiveModel) return "Custom Model";

	const lastSegment = effectiveModel.split("/").pop() ?? effectiveModel;
	return (
		lastSegment
			.replace(/:free$/i, "")
			.replace(/[-_]/g, " ")
			.replace(/\b\w/g, (char) => char.toUpperCase()) || model
	);
}

export function getModelPlaceholder(provider: AiProvider) {
	switch (provider) {
		case "openrouter":
			return `例如：${DEFAULT_OPENROUTER_MODEL}`;
		case "openai":
			return `例如：${DEFAULT_OPENAI_MODEL}`;
		case "openai-compatible":
			return `例如：${DEFAULT_OPENAI_COMPATIBLE_MODEL}`;
		case "anthropic":
			return `例如：${DEFAULT_ANTHROPIC_MODEL}`;
		case "google":
			return `例如：${DEFAULT_GOOGLE_MODEL}`;
		default:
			return "由平台自动选择默认模型";
	}
}

export function getBaseURLPlaceholder(provider: AiProvider) {
	return getDefaultBaseURLForProvider(provider) || "必须填写模型服务接口地址";
}

export function getAiProviderGuide(provider: AiProvider): AiProviderGuide {
	switch (provider) {
		case "server-default":
			return {
				title: "服务端默认配置",
				notes: [
					"平台默认使用服务端 OpenRouter 智能路由，调用服务端配置的 OPENROUTER_API_KEY。",
					"默认优先 deepseek/deepseek-v4-flash；超过服务端 token 阈值后切到 deepseek/deepseek-v4-flash:free。",
				],
				examples: [
					"OPENROUTER_API_KEY=...",
					"AI_MODEL_ROUTER_GLOBAL_TOKEN_LIMIT_PER_HOUR=500000",
				],
			};
		case "openrouter":
			return {
				title: "OpenRouter",
				notes: [
					"适合使用你自己的 OpenRouter Key 接入模型。",
					"自定义模型不会复用平台默认 Key，必须填写 Base URL 和 API Key。",
					"默认模型使用 deepseek/deepseek-v4-flash；如需指定模型，请在测试连接后从模型列表中选择或手动填写。",
				],
				examples: [DEFAULT_OPENROUTER_MODEL, "provider/model-id:free"],
			};
		case "openai":
			return {
				title: "OpenAI",
				notes: [
					"适合直接调用 OpenAI 官方接口。",
					"自定义模型不会复用平台 Key，必须填写 Base URL 和 API Key。",
					"默认使用 GPT-5.4 Mini，兼顾能力、速度和成本；复杂任务可切换 GPT-5.5。",
				],
				examples: [DEFAULT_OPENAI_MODEL, "gpt-5.5", "gpt-5.4-nano"],
			};
		case "openai-compatible":
			return {
				title: "OpenAI Compatible",
				notes: [
					"默认按 DeepSeek V4 配置；DeepSeek 官方 OpenAI 兼容 Base URL 为 https://api.deepseek.com。",
					"Qwen、SiliconFlow、Together、Groq、Ollama、自建网关等也可使用，但必须同步修改 Base URL 与模型名。",
					"deepseek-chat 与 deepseek-reasoner 是旧别名，DeepSeek 官方已公告将于 2026-07-24 停用。",
				],
				examples: [
					"https://api.deepseek.com",
					"deepseek-v4-flash",
					"https://dashscope-intl.aliyuncs.com/compatible-mode/v1",
					"qwen3.6-plus",
					"http://localhost:11434/v1",
				],
			};
		case "anthropic":
			return {
				title: "Anthropic",
				notes: [
					"适合直接调用 Claude 官方接口。",
					"自定义模型不会复用平台 Key，必须填写 Base URL 和 API Key。",
					"默认使用 Claude Sonnet 4.6；更强推理可切换 Opus 4.7，更低延迟可切换 Haiku 4.5。",
				],
				examples: [
					DEFAULT_ANTHROPIC_MODEL,
					"claude-opus-4-7",
					"claude-haiku-4-5",
				],
			};
		case "google":
			return {
				title: "Google",
				notes: [
					"适合调用 Gemini 官方接口。",
					"自定义模型不会复用平台 Key，必须填写 Base URL 和 API Key。",
					"默认使用 Gemini 3 Flash Preview；生产稳定性优先时可切换 Gemini 2.5 Flash Stable。",
				],
				examples: [
					DEFAULT_GOOGLE_MODEL,
					"gemini-3-pro-preview",
					"gemini-2.5-flash",
				],
			};
	}
}

function getDefaultAiClientSettings(): AiClientSettings {
	return {
		provider: DEFAULT_SERVER_PROVIDER,
		apiKey: "",
		model: "",
		baseURL: getDefaultBaseURLForProvider(DEFAULT_SERVER_PROVIDER),
	};
}

export function readAiClientSettings(): AiClientSettings {
	if (typeof window === "undefined") {
		return getDefaultAiClientSettings();
	}

	try {
		const raw = window.localStorage.getItem(AI_SETTINGS_STORAGE_KEY);
		if (!raw) {
			return getDefaultAiClientSettings();
		}

		const parsed = JSON.parse(raw) as Partial<AiClientSettings>;
		const provider =
			parsed.provider && isAiProvider(parsed.provider)
				? parsed.provider
				: DEFAULT_SERVER_PROVIDER;

		return {
			apiKey: parsed.apiKey ?? "",
			model: getEffectiveModelForProvider(provider, parsed.model),
			baseURL:
				typeof parsed.baseURL === "string"
					? parsed.baseURL
					: getDefaultBaseURLForProvider(provider),
			provider,
		};
	} catch {
		return getDefaultAiClientSettings();
	}
}

export function writeAiClientSettings(settings: AiClientSettings) {
	if (typeof window === "undefined") {
		return;
	}

	window.localStorage.setItem(
		AI_SETTINGS_STORAGE_KEY,
		JSON.stringify(settings),
	);
}

function isCustomProvider(
	value?: string,
): value is Exclude<AiProvider, "server-default"> {
	return AI_CUSTOM_PROVIDERS.includes(
		value as (typeof AI_CUSTOM_PROVIDERS)[number],
	);
}

function isAiProvider(value?: string): value is AiProvider {
	return AI_PROVIDER_VALUES.includes(value as AiProvider);
}

export function getConnectionLabel(baseURL: string, provider: AiProvider) {
	try {
		if (baseURL) {
			const url = new URL(baseURL);
			return url.hostname.replace(/^www\./, "");
		}
	} catch {
		// ignore invalid URL and fall back to provider label
	}

	return getProviderLabel(provider);
}

export function readAiCustomConnections(): AiCustomConnection[] {
	if (typeof window === "undefined") {
		return [];
	}

	try {
		const raw = window.localStorage.getItem(AI_CUSTOM_CONNECTIONS_STORAGE_KEY);
		if (!raw) {
			return [];
		}

		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed)) {
			return [];
		}

		return parsed
			.map((item) => {
				const candidate = item as Partial<AiCustomConnection>;
				if (
					typeof candidate.id !== "string" ||
					typeof candidate.label !== "string" ||
					!isCustomProvider(candidate.provider) ||
					typeof candidate.apiKey !== "string" ||
					typeof candidate.baseURL !== "string" ||
					!Array.isArray(candidate.models)
				) {
					return null;
				}

				const models = candidate.models
					.filter((model): model is string => typeof model === "string")
					.map((model) => model.trim())
					.filter(Boolean);

				if (models.length === 0) {
					return null;
				}

				return {
					id: candidate.id,
					label: candidate.label,
					provider: candidate.provider,
					apiKey: candidate.apiKey,
					baseURL: candidate.baseURL,
					models: Array.from(new Set(models)),
					lastValidatedAt:
						typeof candidate.lastValidatedAt === "number"
							? candidate.lastValidatedAt
							: Date.now(),
				} satisfies AiCustomConnection;
			})
			.filter((item): item is AiCustomConnection => item !== null);
	} catch {
		return [];
	}
}

export function writeAiCustomConnections(connections: AiCustomConnection[]) {
	if (typeof window === "undefined") {
		return;
	}

	window.localStorage.setItem(
		AI_CUSTOM_CONNECTIONS_STORAGE_KEY,
		JSON.stringify(connections),
	);
}

export function readAiEntryContext(): AiEntryContext | null {
	if (typeof window === "undefined") {
		return null;
	}

	try {
		const raw = window.localStorage.getItem(AI_ENTRY_CONTEXT_STORAGE_KEY);
		if (!raw) {
			return null;
		}

		const parsed = JSON.parse(raw) as AiEntryContext;
		if (Date.now() - parsed.timestamp > AI_ENTRY_CONTEXT_MAX_AGE_MS) {
			window.localStorage.removeItem(AI_ENTRY_CONTEXT_STORAGE_KEY);
			return null;
		}

		return parsed;
	} catch {
		return null;
	}
}

export function writeAiEntryContext(context: AiEntryContext) {
	if (typeof window === "undefined") {
		return;
	}

	window.localStorage.setItem(
		AI_ENTRY_CONTEXT_STORAGE_KEY,
		JSON.stringify(context),
	);
}
